import React from 'react';

import { Box, Button, Grid } from '@mui/material';
import { omit } from 'es-toolkit/compat';
import { DateTime } from 'luxon';

import { Field, Form } from '@hrlogics/react-components/form';

import { FullscreenLoader } from 'src/components/common';
import {
  AddressFields,
  DateField,
  PhoneField,
  SelectField,
  TextField,
} from 'src/components/finalForm';
import { TIMEZONE_SELECT_OPTIONS } from 'src/constants/time';
import { useAccountPersonalInformation } from 'src/hooks/account';
import { useNotification } from 'src/hooks/app';
import { useCurrentUser, useCurrentUserPermissions } from 'src/hooks/user';
import { validateWithYupSchema } from 'src/utils/yup';

import { PersonalInformationFormFields, PersonalInformationFormValues } from './types';
import {
  getAccountPersonalInformationInitialValues,
  getPersonalInformationFormValidationSchema,
} from './utils';
import { prepareConsumerPersonalInformationData } from './utils/prepareConsumerPersonalInformationData';

export const PersonalInformationForm: React.FC = () => {
  const { user: currentUser, isUserLoading } = useCurrentUser();
  const { isEmployee } = useCurrentUserPermissions();
  const { createApolloContext } = useNotification();

  const {
    accountPersonalInformation,
    updateAccountPersonalInformation,
    loading: personalInformationLoading,
  } = useAccountPersonalInformation(currentUser?.id);

  const initialValues: PersonalInformationFormValues = React.useMemo(() => {
    return getAccountPersonalInformationInitialValues(accountPersonalInformation ?? null);
  }, [accountPersonalInformation]);
  const loading = isUserLoading || personalInformationLoading;

  const onSubmit = React.useCallback(
    async (data: PersonalInformationFormValues) => {
      const apolloContext = createApolloContext({
        successMessage: 'Personal Information has been successfully updated',
      });
      try {
        if (isEmployee) {
          await updateAccountPersonalInformation({
            data: omit(
              prepareConsumerPersonalInformationData(data, accountPersonalInformation?.address?.id),
              [PersonalInformationFormFields.email, PersonalInformationFormFields.workEmail],
            ),
            context: apolloContext,
          });
        } else {
          await updateAccountPersonalInformation({
            data: omit(data, [
              PersonalInformationFormFields.email,
              PersonalInformationFormFields.workEmail,
              PersonalInformationFormFields.homeAddress,
            ]),
            context: apolloContext,
          });
        }
      } catch (error) {
        console.error(error);
      }
    },
    [
      createApolloContext,
      isEmployee,
      updateAccountPersonalInformation,
      accountPersonalInformation?.address,
    ],
  );

  if (loading) {
    return <FullscreenLoader />;
  }

  return (
    <Box>
      <Form
        onSubmit={onSubmit}
        initialValues={initialValues}
        validate={validateWithYupSchema(getPersonalInformationFormValidationSchema(isEmployee))}
      >
        {({ dirty, form, submitting, valid }) => (
          <React.Fragment>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Field
                  name={PersonalInformationFormFields.firstName}
                  fullWidth
                  label="First name"
                  required
                  disabled={submitting}
                  component={TextField}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Field
                  name={PersonalInformationFormFields.lastName}
                  fullWidth
                  component={TextField}
                  required
                  disabled={submitting}
                  label="Last name"
                />
              </Grid>

              {isEmployee && (
                <React.Fragment>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Field
                      name={PersonalInformationFormFields.workEmail}
                      component={TextField}
                      label="Work email"
                      disabled
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Field
                      name={PersonalInformationFormFields.email}
                      component={TextField}
                      label="Personal email"
                      disabled
                      fullWidth
                    />
                  </Grid>
                </React.Fragment>
              )}

              {!isEmployee && (
                <Grid size={12}>
                  <Field
                    name={PersonalInformationFormFields.email}
                    component={TextField}
                    label="Email"
                    disabled
                    fullWidth
                  />
                </Grid>
              )}

              <Grid size={{ xs: 12, md: 6 }}>
                <Field
                  name={PersonalInformationFormFields.birthDate}
                  component={DateField}
                  label="Date of birth"
                  disabled={submitting}
                  {...BIRTH_DATE_FIELD_PROPS}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Field
                  name={PersonalInformationFormFields.phoneNumber}
                  component={PhoneField}
                  label="Phone number"
                  required
                  disabled={submitting}
                  fullWidth
                />
              </Grid>

              {isEmployee && (
                <Grid size={12}>
                  <AddressFields
                    prefix={PersonalInformationFormFields.homeAddress}
                    required
                    disabled={submitting}
                  />
                </Grid>
              )}

              {!isEmployee && (
                <Grid size={12}>
                  <Field
                    name={PersonalInformationFormFields.title}
                    component={TextField}
                    label="Title"
                    disabled={submitting}
                    fullWidth
                  />
                </Grid>
              )}

              <Grid size={12}>
                <Field
                  name={PersonalInformationFormFields.timezone}
                  component={SelectField}
                  disabled={submitting}
                  options={TIMEZONE_SELECT_OPTIONS}
                  label="Timezone"
                  fullWidth
                />
              </Grid>
            </Grid>

            <Box mt={2} display="flex" justifyContent="center" alignItems="center" gap={2}>
              <Button
                color="error"
                disabled={!dirty || submitting}
                loading={submitting}
                size="medium"
                variant="outlined"
                onClick={() => form.reset()}
              >
                Reset
              </Button>

              <Button
                disabled={!dirty || submitting || !valid}
                loading={submitting}
                variant="contained"
                type="submit"
              >
                Save Changes
              </Button>
            </Box>
          </React.Fragment>
        )}
      </Form>
    </Box>
  );
};

const BIRTH_DATE_FIELD_PROPS = {
  InputProps: {
    required: false,
  },
  maxDate: DateTime.now(),
  allowKeyboardControl: true,
  animateYearScrolling: false,
  autoOk: false,
  disableFuture: false,
  disablePast: false,
  disableToolbar: false,
  emptyLabel: '',
  inputVariant: 'outlined',
  invalidLabel: 'unknown',
  maskChar: '_',
  orientation: 'portrait',
  strictCompareDates: false,
  variant: 'inline',
  fullWidth: true,
  mayHaveEmptyValue: true,
};
